// AuthContext.tsx
import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  ReactNode,
} from "react";
import axios from "axios";
import { FetchApi, apiClient } from "../../utils/api";

// 🔹 User type (adjust fields based on your API)
export interface User {
  id?: string;
  firstname?: string;
  lastname?: string;
  email?: string;
  phone?: string;
  [key: string]: any;
}

interface OtpData {
  uuid: string;
  email: string;
}

// 🔹 AuthContext type
interface AuthContextType {
  isLoading: boolean;
  isLoggedIn: boolean;
  user: User;
  otpData: OtpData;
  loginModalVisible: boolean;
  registerModalVisible: boolean;
  otpModalVisible: boolean;
  register: (params: Record<string, any>) => Promise<any>;
  registrationVerify: (params: Record<string, any>) => Promise<any>;
  login: (params: Record<string, any>) => Promise<any>;
  logout: () => boolean;
  forgotPassword: (params: Record<string, any>) => Promise<any>;
  fetchUserProfile: (params: Record<string, any>) => Promise<User>;
  updateUserProfile: (params: Record<string, any>) => Promise<User>;
  onOpenLoginModal: () => void;
  onCloseLoginModal: () => void;
  onOpenRegisterModal: () => void;
  onCloseRegisterModal: () => void;
  onOpenOtpModal: (uuid: string, email: string) => void;
  onCloseOtpModal: () => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(false);
  const [user, setUser] = useState<User>({});
  const [loginModalVisible, setLoginModalVisible] = useState<boolean>(false);
  const [otpModalVisible, setOtpModalVisible] = useState<boolean>(false);
  const [otpData, setOtpData] = useState<
    | {
        uuid: string;
        email: string;
      }
    | any
  >(null);
  const [registerModalVisible, setRegisterModalVisible] =
    useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true); // Add loading state

  // Restore auth state from localStorage
  useEffect(() => {
    const savedUser = localStorage.getItem("auth_user");
    const savedLogin = localStorage.getItem("auth_isLoggedIn");

    if (savedLogin === "true" && savedUser) {
      setIsLoggedIn(true);
      setUser(JSON.parse(savedUser));
    }
    setIsLoading(false); // Mark loading as complete
  }, []);

  const onOpenLoginModal = () => setLoginModalVisible(true);
  const onCloseLoginModal = () => setLoginModalVisible(false);
  const onOpenRegisterModal = () => setRegisterModalVisible(true);
  const onCloseRegisterModal = () => setRegisterModalVisible(false);
  const onOpenOtpModal = (uuid: string, email: string) => {
    setOtpData({ uuid, email });
    setOtpModalVisible(true);
  };
  const onCloseOtpModal = () => {
    setOtpModalVisible(false);
    setOtpData(null);
  };

  const register = useCallback(
    async (parameters: Record<string, any>): Promise<any> => {
      try {
        const url = FetchApi("registration.php", parameters);
        const response = await apiClient.get(url);
        if (response.data.status === 1) return response.data;
        throw response.data;
      } catch (err) {
        throw err;
      }
    },
    []
  );

  const forgotPassword = useCallback(
    async (parameters: Record<string, any>): Promise<any> => {
      try {
        const url = FetchApi("forgot_password", parameters);
        const response = await apiClient.get(url);
        if (response.data.error_code === undefined) return response.data;
        throw response.data;
      } catch (err) {
        throw err;
      }
    },
    []
  );

  const registrationVerify = useCallback(
    async (parameters: Record<string, any>): Promise<any> => {
      try {
        const url = FetchApi("registration_verify.php", parameters);
        const response = await apiClient.get(url);
        if (response.data.status === 1) return response.data;
        throw response.data;
      } catch (err) {
        throw err;
      }
    },
    []
  );

  const login = useCallback(
    async (parameters: Record<string, any>): Promise<any> => {
      try {
        console.log("Login started with params:", parameters);
        const url = FetchApi("member_login.php", parameters);
        const response = await apiClient.get(url);
        console.log("Login response:", response.data);

        if (response.data.status === 1) {
          setIsLoggedIn(true);
          setUser(response.data);
          localStorage.setItem("auth_isLoggedIn", "true");
          console.log("Login successful, fetching profile...");
          await fetchUserProfile(response.data);
          console.log("Profile fetch completed");
          return response.data;
        } else {
          console.log("Login failed:", response.data);
          setIsLoggedIn(false);
          localStorage.removeItem("auth_isLoggedIn");
          localStorage.removeItem("auth_user");
          throw response.data;
        }
      } catch (err) {
        console.log("Login error:", err);
        setIsLoggedIn(false);
        localStorage.removeItem("auth_isLoggedIn");
        localStorage.removeItem("auth_user");
        throw err;
      }
    },
    []
  );

  const fetchUserProfile = useCallback(
    async (parameters: Record<string, any>): Promise<User> => {
      try {
        const url = FetchApi("member_profile.php", parameters);
        const response = await apiClient.get(url);
        if (response.data) {
          setUser(response.data);
          localStorage.setItem("auth_user", JSON.stringify(response.data));
          return response.data;
        } else {
          setUser({});
          localStorage.removeItem("auth_user");
          throw response.data;
        }
      } catch (err) {
        setUser({});
        localStorage.removeItem("auth_user");
        throw err;
      }
    },
    []
  );

  const logout = useCallback((): boolean => {
    try {
      setIsLoggedIn(false);
      setUser({});
      localStorage.removeItem("auth_isLoggedIn");
      localStorage.removeItem("auth_user");
      return true;
    } catch (err) {
      return false;
    }
  }, []);

  // const fetchUserProfile = useCallback(
  //   async (parameters: Record<string, any>): Promise<User> => {
  //     try {
  //       const url = FetchApi("member_profile.php", parameters);
  //       const response = await apiClient.get(url);
  //       if (response.data) {
  //         setUser(response.data);
  //         localStorage.setItem("auth_user", JSON.stringify(response.data));
  //         return response.data;
  //       } else {
  //         setUser({});
  //         localStorage.removeItem("auth_user");
  //         throw response.data;
  //       }
  //     } catch (err) {
  //       setUser({});
  //       localStorage.removeItem("auth_user");
  //       throw err;
  //     }
  //   },
  //   []
  // );

  const updateUserProfile = useCallback(
    async (parameters: Record<string, any>): Promise<User> => {
      try {
        const url = FetchApi("update_profile.php", parameters);
        const response = await apiClient.get(url);
        if (response.data) {
          setUser(response.data);
          localStorage.setItem("auth_user", JSON.stringify(response.data));
          return response.data;
        } else {
          throw response.data;
        }
      } catch (err) {
        throw err;
      }
    },
    []
  );

  const value: AuthContextType = {
    isLoading,
    isLoggedIn,
    user,
    otpData,
    otpModalVisible,
    loginModalVisible,
    registerModalVisible,
    register,
    registrationVerify,
    login,
    logout,
    forgotPassword,
    fetchUserProfile,
    updateUserProfile,
    onOpenLoginModal,
    onCloseLoginModal,
    onOpenRegisterModal,
    onCloseRegisterModal,
    onOpenOtpModal,
    onCloseOtpModal,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within an AuthProvider");
  return context;
};
